import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { User } from "firebase/auth";

/**
 * uids whose profile has been written during this page session.
 *
 * Module-level rather than component state: `ProtectedRoute` remounts on every
 * navigation between protected screens, and this must not turn into a Firestore
 * write per page view. Only successful writes are recorded, so a failure is
 * retried on the next navigation.
 */
const ensured = new Set<string>();

/** Firestore rejects a rule failure with this code, wrapped by our store errors. */
function isPermissionDenied(error: unknown): boolean {
  for (let e: unknown = error, depth = 0; e && depth < 4; depth++) {
    const code = (e as { code?: unknown }).code;
    if (code === "permission-denied" || code === "firestore/permission-denied") return true;
    e = (e as { cause?: unknown }).cause;
  }
  return false;
}

/**
 * Guarantee that a verified user has a profile document, and that their ID
 * token actually says so.
 *
 * Both problems come from the same place. The profile write is skipped at
 * sign-up because the rules reject an unverified write, so it has to happen
 * later — and "later" cannot be pinned to one button, because verification
 * happens in another tab and the user may simply reload the app afterwards.
 * Anchoring it here, where an authenticated user enters the app, covers every
 * route in rather than the two we happened to think of.
 *
 * The retry is the other half. A token minted before verification still carries
 * `email_verified: false`, and rules read the claim from the **token**, not from
 * the user record — so the client can believe it is verified while every read
 * and write is denied. `getIdToken(true)` forces a new one. The queries are then
 * invalidated, because any that already failed are sitting in an error state
 * that will not clear on its own.
 */
export function useEnsureProfile(user: User | undefined): void {
  const client = useQueryClient();
  const running = useRef(false);

  useEffect(() => {
    if (!user?.emailVerified) return;
    if (ensured.has(user.uid) || running.current) return;

    running.current = true;

    void (async () => {
      try {
        // Imported here rather than at module scope: this hook is reached from
        // `ProtectedRoute`, which the public shell renders, and a static import
        // would put the whole Firestore SDK back in the entry chunk.
        const { ensureAppUser } = await import("./users");
        await ensureAppUser(user);
        ensured.add(user.uid);
      } catch (error) {
        if (!isPermissionDenied(error)) return;

        try {
          const { ensureAppUser } = await import("./users");
          await user.getIdToken(true);
          await ensureAppUser(user);
          ensured.add(user.uid);
          // Anything that failed against the stale claim is cached as an error.
          await client.invalidateQueries();
        } catch {
          // Genuinely denied. The screens surface their own error states; there
          // is nothing useful to do from here, and a loop would be worse.
        }
      } finally {
        running.current = false;
      }
    })();
  }, [user, client]);
}
